import type { BrandSlots, HeaderProps } from "@cfg/brand-sdk";
import { useCopy, useLocale } from "@cfg/configurator-ui";
import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type RefObject } from "react";
import { LogoLockup, LogoMark } from "./logo.tsx";

/* -------------------------------------------------------------------------- */
/* Bench effects: one passive, rAF-throttled pointer listener for the whole    */
/* page. It moves the blueprint-grid lens (.bb-bench-lens, a 440 px window     */
/* whose inner grid is counter-translated so the dots stay put) and the        */
/* catalog-card spotlight (.bb-card-spot / .bb-card-ring-spot) with            */
/* `transform` only: no custom properties on :root, so a pointer move restyles */
/* four elements instead of the whole document. Card rects are cached per      */
/* card and dropped on scroll/resize. Fine pointers with motion allowed only;  */
/* touch and reduced motion keep the static top band from CSS. Also publishes  */
/* the header height as --bb-header-h (on resize only) for sticky panels.      */
/* -------------------------------------------------------------------------- */

const LENS = 220; // lens radius, px (matches .bb-bench-lens in css.ts)

function useBenchEffects(bench: RefObject<HTMLDivElement | null>, header: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const root = document.documentElement;
    const el = header.current;
    let ro: ResizeObserver | undefined;
    if (el && typeof ResizeObserver !== "undefined") {
      let last = 0;
      ro = new ResizeObserver(() => {
        const h = Math.round(el.getBoundingClientRect().height);
        if (h !== last) root.style.setProperty("--bb-header-h", `${(last = h)}px`);
      });
      ro.observe(el);
    }

    const benchEl = bench.current;
    const lens = benchEl?.querySelector<HTMLElement>(".bb-bench-lens");
    const grid = lens?.firstElementChild as HTMLElement | null | undefined;
    if (!benchEl || !lens || !grid) return () => ro?.disconnect();

    const fine = window.matchMedia("(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)");
    let frame = 0;
    let x = 0;
    let y = 0;
    let live = false;
    let stale = false; // scrolled since the last paint: re-hit-test the card under the pointer
    let card: HTMLElement | null = null;
    let rect: DOMRect | null = null;
    let spot: HTMLElement | null = null;
    let ring: HTMLElement | null = null;

    const setCard = (next: HTMLElement | null) => {
      if (next === card) return;
      card = next;
      rect = null;
      spot = next?.querySelector<HTMLElement>(".bb-card-spot") ?? null;
      ring = next?.querySelector<HTMLElement>(".bb-card-ring-spot") ?? null;
    };
    const paint = () => {
      frame = 0;
      lens.style.transform = `translate3d(${x - LENS}px, ${y - LENS}px, 0)`;
      grid.style.transform = `translate3d(${LENS - x}px, ${LENS - y}px, 0)`;
      if (stale) {
        stale = false;
        const hit = document.elementFromPoint(x, y);
        setCard(hit ? (hit.closest(".bb-card") as HTMLElement | null) : null);
      }
      if (!card) return;
      if (!rect) rect = card.getBoundingClientRect();
      const t = `translate3d(${x - rect.left - rect.width / 2}px, ${y - rect.top}px, 0)`;
      if (spot) spot.style.transform = t;
      if (ring) ring.style.transform = t;
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(paint);
    };
    const show = (on: boolean) => {
      if (on === live) return;
      live = on;
      benchEl.classList.toggle("is-live", on);
    };
    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== "mouse" && e.pointerType !== "pen") return;
      x = e.clientX;
      y = e.clientY;
      const t = e.target;
      setCard(t instanceof Element ? (t.closest(".bb-card") as HTMLElement | null) : null);
      show(true);
      schedule();
    };
    const onOut = (e: PointerEvent) => {
      if (!e.relatedTarget) show(false); // left the window
    };
    const onHide = () => show(false);
    const onVisibility = () => {
      if (document.visibilityState !== "visible") show(false);
    };
    const onScroll = () => {
      rect = null;
      if (!live) return;
      stale = true;
      schedule();
    };
    const onResize = () => {
      rect = null;
    };
    const unbind = () => {
      window.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerout", onOut);
      window.removeEventListener("blur", onHide);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
    const bind = () => {
      unbind();
      if (fine.matches) {
        window.addEventListener("pointermove", onMove, { passive: true });
        document.addEventListener("pointerout", onOut, { passive: true });
        window.addEventListener("blur", onHide);
        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("scroll", onScroll, { passive: true, capture: true });
        window.addEventListener("resize", onResize, { passive: true });
      } else {
        show(false);
      }
    };
    bind();
    fine.addEventListener?.("change", bind);
    return () => {
      ro?.disconnect();
      fine.removeEventListener?.("change", bind);
      unbind();
      if (frame) cancelAnimationFrame(frame);
      show(false);
    };
  }, [bench, header]);
}

/**
 * Nav: the ink (raised pill + orange tick) rests on the active page only; a
 * separate quiet hover pill follows hover and keyboard focus and fades out on
 * leave.
 */
function useNavInk(nav: RefObject<HTMLElement | null>, activeHref: string | undefined, itemsKey: string) {
  useLayoutEffect(() => {
    const el = nav.current;
    if (!el) return;
    const hover = el.querySelector<HTMLElement>(".bb-nav-hover");
    const active = () => el.querySelector<HTMLElement>(".bb-nav-link.is-active");
    const placeInk = () => {
      const link = active();
      if (!link) return el.style.setProperty("--bb-ink-o", "0");
      el.style.setProperty("--bb-ink-x", `${link.offsetLeft}px`);
      el.style.setProperty("--bb-ink-w", `${link.offsetWidth}px`);
      el.style.setProperty("--bb-ink-o", "1");
    };
    placeInk();
    // Bring the current section into view when the nav scrolls on narrow screens.
    const a = active();
    if (a && el.scrollWidth > el.clientWidth) el.scrollLeft = Math.max(0, a.offsetLeft - 16);
    const ready = requestAnimationFrame(() => el.classList.add("is-ready"));

    let shown = false;
    const placeHover = (link: HTMLElement | null) => {
      if (!hover) return;
      if (!link || link.classList.contains("is-active")) {
        hover.classList.remove("is-on");
        return;
      }
      // First appearance jumps into place (no slide in from the last spot); later moves glide.
      hover.classList.toggle("is-snap", !shown);
      hover.style.transform = `translateX(${link.offsetLeft}px)`;
      hover.style.width = `${link.offsetWidth}px`;
      hover.classList.add("is-on");
      shown = true;
    };
    const over = (e: Event) => {
      const link = (e.target as Element).closest?.(".bb-nav-link") as HTMLElement | null;
      if (link) placeHover(link);
    };
    const leave = () => {
      shown = false;
      placeHover(null);
    };
    const onFocusOut = (e: FocusEvent) => {
      if (!el.contains(e.relatedTarget as Node | null)) leave();
    };
    // Links change width without the nav doing so (web font arriving, another language,
    // stretched links sharing the row on narrow screens), so watch them too.
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(placeInk) : undefined;
    ro?.observe(el);
    el.querySelectorAll(".bb-nav-link").forEach((l) => ro?.observe(l));
    void document.fonts?.ready.then(placeInk);
    el.addEventListener("pointerover", over, { passive: true });
    el.addEventListener("pointerleave", leave, { passive: true });
    el.addEventListener("focusin", over);
    el.addEventListener("focusout", onFocusOut);
    return () => {
      cancelAnimationFrame(ready);
      ro?.disconnect();
      el.removeEventListener("pointerover", over);
      el.removeEventListener("pointerleave", leave);
      el.removeEventListener("focusin", over);
      el.removeEventListener("focusout", onFocusOut);
    };
  }, [nav, activeHref, itemsKey]);
}

/** Card entrance: a short stagger on the first list a visitor sees this session, then a plain fade. */
function useFirstVisit() {
  useLayoutEffect(() => {
    let seen = false;
    try {
      seen = sessionStorage.getItem("bb-seen") === "1";
      if (!seen) sessionStorage.setItem("bb-seen", "1");
    } catch {
      seen = false;
    }
    if (!seen) document.documentElement.classList.add("bb-first");
  }, []);
}

/** Skip link target: the shell's <main>; focus it without leaving a hash in the URL. */
function skipToMain(e: MouseEvent<HTMLAnchorElement>) {
  const main = document.querySelector<HTMLElement>("main, .app-main");
  if (!main) return;
  e.preventDefault();
  if (!main.hasAttribute("tabindex")) main.setAttribute("tabindex", "-1");
  main.focus();
  main.scrollIntoView({ block: "start" });
}

/* ---------------------------------- slots --------------------------------- */

/** Full lockup, or the [••] mark alone on very narrow screens (CSS picks one). Named by the SVG. */
const Logo: BrandSlots["Logo"] = ({ brand }) => (
  <>
    <LogoLockup title={brand.name} />
    <LogoMark className="bb-logo bb-logo-mark" title={brand.name} />
  </>
);

function initials(name: string | undefined, email: string | undefined): string {
  const s = (name || email || "?").trim();
  return s.slice(0, 1).toUpperCase();
}

const Globe = () => (
  <svg className="bb-lang-icon" viewBox="0 0 16 16" width="14" height="14" focusable="false" aria-hidden>
    <circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.3" />
    <path d="M1.75 8h12.5M8 1.75c1.9 1.8 2.8 3.9 2.8 6.25S9.9 12.45 8 14.25C6.1 12.45 5.2 10.35 5.2 8S6.1 3.55 8 1.75Z" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
  </svg>
);

/** Door-and-arrow: phones show only this for "Sign out" (the text stays for screen readers). */
const SignOutIcon = () => (
  <svg className="bb-signout-icon" viewBox="0 0 16 16" width="18" height="18" focusable="false" aria-hidden>
    <path d="M6.5 2.5H3.25v11H6.5M10.5 5l3 3-3 3M13.25 8H6.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * Language switcher: a quiet pill (globe + native language name, the code on phones) that opens a
 * small menu styled like the rest of the header, instead of the browser's native select list. Only
 * appears when the brand offers several locales. Keyboard: Enter, Space or the arrows open it; the
 * arrows, Home and End move; Enter or Space picks; Escape or Tab closes. A click outside closes it.
 */
function LanguageSwitcher({ locale }: { locale: NonNullable<HeaderProps["locale"]> }) {
  const copy = useCopy();
  const [open, setOpen] = useState(false);
  const current = Math.max(0, locale.options.findIndex((o) => o.code === locale.current));
  const [focus, setFocus] = useState(current);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const id = useId();

  const show = (at = current) => {
    setFocus(at);
    setOpen(true);
  };
  const close = (refocus = true) => {
    setOpen(false);
    if (refocus) button.current?.focus();
  };
  const pick = (i: number) => {
    const o = locale.options[i];
    if (o && o.code !== locale.current) locale.onChange(o.code);
    close();
  };

  useEffect(() => {
    if (!open) return;
    list.current?.focus();
    const onDown = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  const onButtonKey = (e: KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      show(e.key === "ArrowUp" ? locale.options.length - 1 : current);
    }
  };
  const onListKey = (e: KeyboardEvent) => {
    const last = locale.options.length - 1;
    const move = { ArrowDown: Math.min(last, focus + 1), ArrowUp: Math.max(0, focus - 1), Home: 0, End: last }[e.key];
    if (move !== undefined) {
      e.preventDefault();
      setFocus(move);
    } else if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      pick(focus);
    } else if (e.key === "Escape") {
      e.preventDefault();
      close();
    } else if (e.key === "Tab") {
      close(false);
    }
  };

  return (
    <div className="bb-lang" ref={root}>
      <button
        ref={button}
        type="button"
        className="bb-lang-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? `${id}-list` : undefined}
        aria-label={`${copy("locale.label")}: ${locale.options[current]?.label ?? locale.current}`}
        onClick={() => (open ? close() : show())}
        onKeyDown={onButtonKey}
      >
        <Globe />
        <span className="bb-lang-name">{locale.options[current]?.label ?? locale.current}</span>
        {/* Phones show just the language code ("EN"). */}
        <span className="bb-lang-code" aria-hidden>
          {locale.current.split("-")[0].toUpperCase()}
        </span>
      </button>
      {open && (
        <ul
          ref={list}
          id={`${id}-list`}
          className="bb-lang-menu"
          role="listbox"
          tabIndex={-1}
          aria-label={copy("locale.label")}
          aria-activedescendant={`${id}-opt-${focus}`}
          onKeyDown={onListKey}
        >
          {locale.options.map((o, i) => (
            <li
              key={o.code}
              id={`${id}-opt-${i}`}
              role="option"
              lang={o.code}
              aria-selected={i === current}
              className={`bb-lang-option${i === focus ? " is-focus" : ""}`}
              onPointerEnter={() => setFocus(i)}
              onClick={() => pick(i)}
            >
              {o.label}
              {i === current && <Check />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const Check = () => (
  <svg className="bb-lang-check" viewBox="0 0 16 16" width="14" height="14" focusable="false" aria-hidden>
    <path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Header: BrandSlots["Header"] = ({ brand, nav, user, onSignIn, onSignOut, locale }) => {
  const copy = useCopy();
  const benchRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const navRef = useRef<HTMLElement>(null);
  useBenchEffects(benchRef, headerRef);
  useNavInk(
    navRef,
    nav.find((n) => n.active)?.href,
    nav.map((n) => `${n.href}|${n.label}`).join(","),
  );
  useFirstVisit();
  const mineHref = nav.find((n) => n.href === "/mine")?.href;
  const who = user ? (user.name ?? user.email ?? copy("auth.account")) : "";
  const signedInAs = user?.email ? copy("auth.signedInAs", { email: user.email }) : undefined;
  return (
    <>
      <div className="bb-bench" ref={benchRef} aria-hidden>
        <div className="bb-bench-clip">
          <div className="bb-bench-lens">
            <div className="bb-bench-grid" />
          </div>
        </div>
      </div>
      <a className="bb-skip" href="#main" onClick={skipToMain}>
        {copy("a11y.skipToContent")}
      </a>
      <header className="bb-header" ref={headerRef}>
        <a className="bb-header-brand" href="/">
          <Logo brand={brand} />
        </a>
        <nav className="bb-nav" aria-label={copy("nav.primaryLabel")} ref={navRef}>
          <span className="bb-nav-ink" aria-hidden />
          <span className="bb-nav-hover" aria-hidden />
          {nav.map((n) => (
            <a key={n.href} href={n.href} className={`bb-nav-link${n.active ? " is-active" : ""}`} aria-current={n.active ? "page" : undefined}>
              {n.label}
            </a>
          ))}
        </nav>
        <div className="bb-account">
          {locale && locale.options.length > 1 && <LanguageSwitcher locale={locale} />}
          {user ? (
            <>
              {mineHref ? (
                <a className="bb-account-btn" href={mineHref} title={signedInAs}>
                  {user.picture ? <img className="bb-avatar" src={user.picture} alt="" width={26} height={26} /> : <span className="bb-avatar" aria-hidden>{initials(user.name, user.email)}</span>}
                  <span className="bb-account-name">{who}</span>
                </a>
              ) : (
                <span className="bb-account-btn is-static" title={signedInAs}>
                  {user.picture ? <img className="bb-avatar" src={user.picture} alt="" width={26} height={26} /> : <span className="bb-avatar" aria-hidden>{initials(user.name, user.email)}</span>}
                  <span className="bb-account-name">{who}</span>
                </span>
              )}
              <button type="button" className="bb-signout" title={copy("auth.signOut")} onClick={onSignOut}>
                <SignOutIcon />
                <span className="bb-signout-text">{copy("auth.signOut")}</span>
              </button>
            </>
          ) : (
            <button type="button" className="bb-account-btn is-signin" onClick={onSignIn}>
              {copy("auth.signIn")}
            </button>
          )}
        </div>
      </header>
    </>
  );
};

/** Only the LEGO® trademark disclaimer: the site names LEGO sets, so it must say it isn't LEGO's. */
const Footer: BrandSlots["Footer"] = ({ brand, links, attribution }) =>
  brand.fitTags.disclaimer || links?.length || attribution ? (
    <footer className="bb-footer">
      {!!links?.length && (
        <nav className="bb-footer-links" aria-label={brand.name}>
          {links.map((l) => (
            <a key={l.href} href={l.href}>
              {l.label}
            </a>
          ))}
        </nav>
      )}
      {attribution && <p className="bb-footer-attribution">{attribution}</p>}
      {brand.fitTags.disclaimer && <p className="bb-footer-legal">{brand.fitTags.disclaimer}</p>}
    </footer>
  ) : null;

const Arrow = () => (
  <svg viewBox="0 0 16 16" width="14" height="14" focusable="false" aria-hidden>
    <path d="M3 8h9M8.5 4.5 12 8l-3.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CatalogCard: BrandSlots["CatalogCard"] = ({ entry, children }) => {
  const copy = useCopy();
  const { locale } = useLocale();
  const community = entry.kind === "community";
  const cta = entry.href.startsWith("/design/") ? copy("card.configure") : copy("card.view");
  const byline = community ? [entry.designTitle, entry.taggers?.length && copy("community.taggers", { names: entry.taggers.join(", ") })].filter(Boolean).join(" · ") : "";
  const fits = community ? (entry.fits ?? []) : [];
  return (
    <a className={`bb-card${community ? " is-community" : ""}`} href={entry.href}>
      <span className="bb-card-ring" aria-hidden>
        <span className="bb-card-ring-spot" />
      </span>
      <span className="bb-card-fx" aria-hidden>
        <span className="bb-card-spot" />
      </span>
      <div className="bb-card-preview">
        {children ?? (entry.thumbnailUrl ? <img src={entry.thumbnailUrl} alt="" loading="lazy" decoding="async" /> : <span className="bb-card-placeholder">{entry.title.slice(0, 2)}</span>)}
      </div>
      <div className="bb-card-body">
        <div className="bb-card-head">
          <h3 className="bb-card-title">{entry.title}</h3>
        </div>
        {community && byline ? <p className="bb-card-desc bb-card-byline">{byline}</p> : entry.description && <p className="bb-card-desc">{entry.description}</p>}
        {entry.variants && (
          <ul className="bb-card-variants" aria-label={copy("family.label")}>
            {entry.variants.map((v) => (
              <li key={v} className="bb-card-variant">
                {v}
              </li>
            ))}
          </ul>
        )}
        {fits.length > 0 && (
          <ul className="bb-card-fits" aria-label={entry.fitLabel ? copy("card.fitsLabel", { label: entry.fitLabel }) : copy("card.fits")}>
            {fits.slice(0, 3).map((f) => (
              <li key={f.ref} className="bb-fit">
                <span className="bb-fit-ref">{f.ref}</span>
                {f.name && <span className="bb-fit-name">{f.name}</span>}
              </li>
            ))}
            {fits.length > 3 && <li className="bb-fit bb-fit-more">+{fits.length - 3}</li>}
          </ul>
        )}
        <div className="bb-card-foot">
          <div className="bb-card-meta">
            {community ? (
              <>
                {!!entry.printerCount && <span className="bb-signal">✓ {entry.printerCount === 1 ? copy("community.printers.one") : copy("community.printers.other", { count: entry.printerCount.toLocaleString(locale) })}</span>}
              </>
            ) : (
              <>
                {!!entry.downloads && (
                  <span className="bb-downloads">↓ {entry.downloads === 1 ? copy("downloads.one") : copy("downloads.other", { count: entry.downloads.toLocaleString(locale) })}</span>
                )}
              </>
            )}
          </div>
          <span className="bb-card-cta" aria-hidden>
            {cta}
            <Arrow />
          </span>
        </div>
      </div>
    </a>
  );
};

const EmptyState: BrandSlots["EmptyState"] = ({ title, body, action }) => (
  <div className="bb-empty">
    <LogoMark />
    <h2>{title}</h2>
    {body && <p>{body}</p>}
    {action}
  </div>
);

const ConfiguratorFrame: BrandSlots["ConfiguratorFrame"] = ({ children, aside }) => (
  <div className="bb-frame">
    <div className="bb-frame-main">{children}</div>
    {aside && <aside className="bb-frame-aside">{aside}</aside>}
  </div>
);

export const slots: Partial<BrandSlots> = { Logo, Header, Footer, CatalogCard, EmptyState, ConfiguratorFrame };
